import { Badge, Button, CircularProgress, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme } from "@mui/material";
import {
    IconGridDots,
    IconFilter,
    IconSortAscending2,
    IconSortDescending2,
    IconPlaylistAdd
} from "@tabler/icons-react";
import { getPager } from "helper/common";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import {
    clearLabRecordList,
    closeLabRecordsFilterDialog,
    getLabRecordList,
    openLabRecordsFilterDialog,
    setLabRecordsFilteredData,
    labRecordListErrorClear,
    closeLabRecordCreateDialog,
    openLabRecordCreateDialog
} from "store/app/lab-management/lab-records/actions";
import MainCard from "ui-component/cards/MainCard";
import SubCard from "ui-component/cards/SubCard";
import { NoData } from "ui-component/custom-components/nodata";
import { VWPagination } from "ui-component/custom-components/pagination";
import FilterLabRecords from "./filter";
import CreateLabRecords from "./create";
import moment from "moment";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import Chip from "ui-component/extended/Chip";

const tableHeaderStyle = { paddingLeft: '0px', textAlign: "left" };

const TruncatedHeaderCell = ({ type, children }) => {
    const ref = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const check = () => {
            setShowTooltip(el.scrollWidth > el.clientWidth);
        };

        check();

        const observer = new ResizeObserver(check);
        observer.observe(el);

        return () => observer.disconnect();
    }, [children]);

    return (
        <TableCell sx={{ maxWidth: type === "header" ? 200 : 350 }}>
            <Tooltip title={showTooltip ? children : ''} arrow placement="top">
                <Typography
                    ref={ref}
                    variant={type === "header" ? "h5" : "inherit"}
                    noWrap
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: showTooltip ? "pointer" : ""
                    }}
                >
                    {children}
                </Typography>
            </Tooltip>
        </TableCell>
    );
};

const LabRecordsList = (props) => {
    //--------------------List--------------------------------------
    const { labRecordList, totalCounts, isPending, errorMessage } = props;

    const [pageSize, setPageSize] = useState(10);
    const [Pager, setPager] = useState({ currentPage: 1 });
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortOrder, setSortOrder] = useState(null);

    //--------------------Filter------------------------------------
    const { labRecordsFilterData, isLabRecordsFilterDialogOpen } = props;
    const [searchParams] = useSearchParams();

    const filterObject = Object.fromEntries([...searchParams.entries()].map(([key, value]) => [
        key?.toLowerCase(),
        value?.toLowerCase(),
    ]));
    //--------------------Create------------------------------------
    const { isLabRecordCreateDialogOpen } = props;
    //--------------------Profile-----------------------------------
    const { userProfile } = props;
    const { userRoleName } = userProfile || {};

    //--------------------UI Design---------------------------------
    const theme = useTheme();
    const colorPrimary = theme.palette.secondary.dark;
    const [tableHeight, setTableHeight] = useState(window.innerHeight);

    useEffect(() => {
        props.labRecordListErrorClear();
        if (filterObject && Object.keys(filterObject).length > 0) {
            delete filterObject.src;
            props.setLabRecordsFilteredData(filterObject);
        }

        const handleResize = () => {
            setTableHeight(window.innerHeight)
        };
        window.addEventListener("resize", handleResize);

        return () => {
            props.clearLabRecordList();
            window.removeEventListener("resize", handleResize);
        }
    }, [])

    const handleSort = (column) => {
        if (sortOrder === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortOrder(column);
            setSortDirection('asc');
        }
    };

    const setPage = (page = 1) => {
        props.getLabRecordList(page, pageSize, sortOrder, sortDirection, labRecordsFilterData);

        setPager(prevPager => {
            const totalPages = Math.ceil(totalCounts / pageSize);
            const visiblePages = 4;
            const pageOffset = Math.max(0, Math.floor((page - 1) / visiblePages) * visiblePages);
            const newPages = [];
            for (let i = pageOffset + 1; i <= Math.min(totalPages, pageOffset + visiblePages); i++) {
                newPages.push(i);
            }
            return {
                ...prevPager,
                currentPage: page,
                totalPages,
                pages: newPages,
            }
        })
    }

    useEffect(() => {
        const currentPager = getPager(totalCounts, 1, pageSize);
        setPager(currentPager)
    }, [totalCounts]);

    useEffect(() => {
        setPage();
    }, [labRecordsFilterData, sortOrder, sortDirection, pageSize])

    const handleLabRecordCreateOpen = () => {
        props.openLabRecordCreateDialog();
    }
    const handleLabRecordCreateClose = () => {
        props.closeLabRecordCreateDialog();
    }

    const handleFilterOpen = () => {
        props.openLabRecordsFilterDialog();
    }
    const handleFilterClose = () => {
        props.closeLabRecordsFilterDialog();
    }

    return (
        <>
            <SubCard sx={{
                p: 1.5,
                pl: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}
            >
                <Grid item sx={{ display: 'flex', gap: { lg: 2, md: 2, sm: 1, xs: 1 }, alignItems: "center", marginRight: "10px" }}>
                    <Typography variant="h4">Lab Records List - </Typography>
                    <Tooltip title="Lab Record Count" placement="top">
                        <span>
                            <Chip
                                label={totalCounts}
                                chipcolor="secondary"
                                size="small"
                                sx={{
                                    borderRadius: "20%",
                                    py: 1,
                                    fontWeight: "bold",
                                    fontSize: 15,
                                    cursor: "pointer"
                                }}
                            />
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                    <Badge
                        badgeContent={labRecordsFilterData ? Object.keys(labRecordsFilterData).length : 0}
                        color="secondary"
                        invisible={labRecordsFilterData ? Object.keys(labRecordsFilterData).length === 0 : true}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleFilterOpen}
                            startIcon={<IconFilter />}
                        >
                            Filter
                        </Button>
                    </Badge>
                    {(userRoleName === "Lab Admin" || userRoleName === "Lab User") &&
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleLabRecordCreateOpen}
                            startIcon={<IconPlaylistAdd />}
                        >
                            Create Lab Records
                        </Button>}
                </Grid>
            </SubCard>
            <MainCard sx={{ marginTop: '15px' }}>
                {errorMessage && <ErrorAlert type={'error'} content={errorMessage} />}
                <TableContainer sx={{ whiteSpace: "nowrap", maxHeight: `calc(${tableHeight}px - 380px)`, overflow: "auto" }}>
                    <Table size="small" stickyHeader aria-label="sticky table"
                        sx={{
                            '& tr > *:last-child': {
                                position: 'sticky',
                                right: 0,
                                bgcolor: 'white',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('LabRecordsIndex')}
                                        endIcon={sortOrder === 'LabRecordsIndex' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Lab Record No.
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('ReportName')}
                                        endIcon={sortOrder === 'ReportName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Report Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('FarmRecordIndex')}
                                        endIcon={sortOrder === 'FarmRecordIndex' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Farm Record No.
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('FarmName')}
                                        endIcon={sortOrder === 'FarmName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Farm Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('Observation')}
                                        endIcon={sortOrder === 'Observation' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Observation
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('TechnicalManagerUserName')}
                                        endIcon={sortOrder === 'TechnicalManagerUserName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Technical Manager
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('CustomerName')}
                                        endIcon={sortOrder === 'CustomerName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Customer
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('SubmittedUserName')}
                                        endIcon={sortOrder === 'SubmittedUserName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Submitted By
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('InspectionDate')}
                                        endIcon={sortOrder === 'InspectionDate' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Inspection Date
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('SubmittedTimeStamp')}
                                        endIcon={sortOrder === 'SubmittedTimeStamp' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Submitted On
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('Location')}
                                        endIcon={sortOrder === 'Location' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Location
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('RegionName')}
                                        endIcon={sortOrder === 'RegionName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Region Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('StateName')}
                                        endIcon={sortOrder === 'StateName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        State/Export
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('DistrictName')}
                                        endIcon={sortOrder === 'DistrictName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        District
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('Anticoccidial')}
                                        endIcon={sortOrder === 'Anticoccidial' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Anticoccidial
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('Agp')}
                                        endIcon={sortOrder === 'Agp' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        AGP &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('Probiotic')}
                                        endIcon={sortOrder === 'Probiotic' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Probiotic
                                    </Button>
                                </TableCell>
                                <TableCell>View</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(labRecordList?.length > 0) && !isPending ?
                                labRecordList?.map((data, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{data.LabRecordsIndex || 'N/A'}</TableCell>
                                        <TableCell>{data.ReportName || 'N/A'}</TableCell>
                                        <TableCell>{data.FarmRecordIndex || 'N/A'}</TableCell>
                                        <TableCell>{data.FarmName || 'N/A'}</TableCell>
                                        <TruncatedHeaderCell type="body">{data.Observation || 'N/A'}</TruncatedHeaderCell>
                                        <TableCell>
                                            {data.TechnicalManagerUserName || 'N/A'}<br />
                                            <small> {data.TechnicalManagerUserEmail || 'N/A'}</small>
                                        </TableCell>
                                        <TableCell>
                                            {data.CustomerName || 'N/A'}<br />
                                            <small>{data.CustomerEmail || 'N/A'}</small>
                                        </TableCell>
                                        <TableCell>
                                            {data.SubmittedUserName || 'N/A'}<br />
                                            <small>{data.SubmittedUserEmail || 'N/A'}</small>
                                        </TableCell>
                                        <TableCell>{data.InspectionDate ? moment(data.InspectionDate).format("DD MMM YYYY") : 'N/A'}</TableCell>
                                        <TableCell>{data.SubmittedTimeStamp ? moment(data.SubmittedTimeStamp).format("DD MMM YYYY") : 'N/A'}</TableCell>
                                        <TruncatedHeaderCell type="body">{data.Location || 'N/A'}</TruncatedHeaderCell>
                                        <TableCell>{data.RegionName || 'N/A'}</TableCell>
                                        <TableCell>{data.StateName || 'N/A'}</TableCell>
                                        <TableCell>{data.DistrictName || 'N/A'}</TableCell>
                                        <TableCell>{data.Anticoccidial || 'N/A'}</TableCell>
                                        <TableCell>{data.Agp || 'N/A'}</TableCell>
                                        <TableCell>{data.Probiotic || 'N/A'}</TableCell>
                                        <TableCell>
                                            <Link to={`/lab-records/details/${data.Id}`}>
                                                <IconGridDots color={theme.palette.primary.main} />
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))
                                : isPending ?
                                    <TableRow >
                                        <TableCell colSpan={11} height='300px'>
                                            <Grid container alignItems={"center"} justifyContent={"center"} gap={1}
                                                sx={{
                                                    position: "sticky",
                                                    width: "fit-content",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)"
                                                }}
                                            >
                                                <CircularProgress />
                                                <Typography variant="h5" color={'primary'}> Loading...</Typography>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                    : <TableCell colSpan={11} height='300px'>
                                        <NoData />
                                    </TableCell>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {(totalCounts > 0) &&
                    <Grid>
                        <VWPagination
                            pageDetails={Pager}
                            pageChange={setPage}
                            totalRecords={totalCounts}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                        />
                    </Grid>
                }
            </MainCard>
            <br />
            <CreateLabRecords
                filter={labRecordsFilterData}
                paginationData={{
                    page: (Pager && Pager.currentPage) || 1,
                    pageSize: pageSize,
                    sortDirection: sortDirection,
                    sortOrder: sortOrder
                }}
                open={isLabRecordCreateDialogOpen}
                onClose={handleLabRecordCreateClose}
            />
            <FilterLabRecords
                open={isLabRecordsFilterDialogOpen}
                onClose={handleFilterClose}
                setPage={setPage}
            />
        </>
    )
}

const mapStateToProps = (state) => ({
    //--------------------List--------------------------------------
    labRecordList: state.labRecords.labRecordList,
    totalCounts: state.labRecords.labRecordListCount,
    isPending: state.labRecords.isLabRecordListPending,
    errorMessage: state.labRecords.labRecordListErrorMessage,
    //--------------------Filter------------------------------------
    isLabRecordsFilterDialogOpen: state.labRecords.isLabRecordsFilterDialogOpen,
    labRecordsFilterData: state.labRecords.labRecordsFilterData,
    //--------------------Create------------------------------------
    isLabRecordCreateDialogOpen: state.labRecords.isLabRecordCreateDialogOpen,
    //--------------------Profile-----------------------------------
    userProfile: state.login.userProfile,
})

const mapDispatchToProps = (dispatch) => ({
    //--------------------List--------------------------------------
    getLabRecordList: (page, pageSize, sortOrder, sortDirection, filter) => dispatch(getLabRecordList(page, pageSize, sortOrder, sortDirection, filter)),
    clearLabRecordList: () => dispatch(clearLabRecordList()),
    labRecordListErrorClear: () => dispatch(labRecordListErrorClear()),
    //--------------------Filter------------------------------------
    openLabRecordsFilterDialog: () => dispatch(openLabRecordsFilterDialog()),
    closeLabRecordsFilterDialog: () => dispatch(closeLabRecordsFilterDialog()),
    setLabRecordsFilteredData: (filter) => dispatch(setLabRecordsFilteredData(filter)),
    //--------------------Create------------------------------------
    openLabRecordCreateDialog: () => dispatch(openLabRecordCreateDialog()),
    closeLabRecordCreateDialog: () => dispatch(closeLabRecordCreateDialog()),
})
export default connect(mapStateToProps, mapDispatchToProps)(LabRecordsList);